import { useMutation, useQueryClient } from '@tanstack/react-query'
import { usePaginatedQuery } from './usePaginatedQuery'
import { useAuth } from '@/contexts/AuthContext'
import { accountApiService, accountQueries } from '@/services/accountApi'
import type { AccountPhoneNumber, AccountPhoneNumbersResponse } from '@/types/account'

export const VERIFIED_PHONE_NUMBERS_PAGE_SIZE = 5

export const useVerifiedPhoneNumbersQuery = () => {
  const { isAuthenticated } = useAuth()

  return usePaginatedQuery<AccountPhoneNumbersResponse, AccountPhoneNumber>({
    queryKey: accountQueries.verifiedPhoneNumbersBase,
    queryFn: (page, pageSize) => accountApiService.getVerifiedPhoneNumbers(page, pageSize),
    transform: (data) => ({
      items: data.phoneNumbers,
      totalItems: data.totalItems,
      totalPages: data.totalPages,
    }),
    pageSize: VERIFIED_PHONE_NUMBERS_PAGE_SIZE,
    enabled: isAuthenticated,
  })
}

export const useTriggerPhoneVerification = () => {
  return useMutation({
    mutationFn: (phoneNumber: string) =>
      accountApiService.triggerPhoneVerification(phoneNumber),
  })
}

export const useConfirmPhoneVerification = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ phoneNumber, code }: { phoneNumber: string; code: string }) =>
      accountApiService.confirmPhoneVerification(phoneNumber, code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountQueries.verifiedPhoneNumbersBase })
    },
  })
}

export const useRemoveVerifiedPhoneNumber = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (phoneNumber: string) =>
      accountApiService.removeVerifiedPhoneNumber(phoneNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountQueries.verifiedPhoneNumbersBase })
    },
  })
}
